/**
 * EQUIPMENT HIRE ON A WORK ORDER — 0214.
 *
 * A hire order is paid for the days its machines are on site, read from a
 * daily hire log sheet the Site Engineer keeps and the Resident Construction
 * Manager certifies. This card shows, line by line, the days ordered, the days
 * variations allowed and the paid days certified so far, and lists the order's
 * log sheets with the button to open this month's.
 *
 * It draws NOTHING unless the order has a hire line, so every other work order
 * on the register looks exactly as it did.
 *
 * Why not the "Lines" table above it: that table reads v_wo_line_progress,
 * whose "Certified" column is the Measurement Book. A hire line is never
 * measured, so it would read zero there. Changing that view would change every
 * other order's screen; a card of its own changes none.
 */
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { CalendarDays, Plus, Truck } from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { dbMessage, useQuery } from '@/lib/data'
import { supabase } from '@/lib/supabase'
import { qty, rupees } from '@/lib/format'
import { Card, Field, Input, Modal, StatusPill, useToast } from '@/components/ui'
import { serviceCan } from '../nav'
import { gstPayer, gstSplit, withGst, type GstHeader } from '../lib/gst'
import { LineGstTable, LinesViewSwitch, useLinesView } from './LineGst'

const n = (v: unknown) => Number(v ?? 0) || 0

/** Orders a machine may be logged against. The same list rpc_hire_log_open uses. */
export const HIRE_LIVE = ['APPROVED', 'VENDACK', 'PARTIALLY_FULFILLED', 'FULFILLED']

/** This month, as an <input type="month"> wants it. */
export function thisMonth(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

/** "Mon YYYY" from a YYYY-MM-DD month start. */
export function monthLabel(iso: string | null | undefined): string {
  if (!iso) return '—'
  const [y, m] = iso.slice(0, 7).split('-').map(Number)
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })
}

/** How a hire line was quoted, in words: "₹1,20,000 per month". */
export function quotedLabel(l: { hire_quoted_rate?: number | null; hire_rate_basis?: string | null }) {
  if (!l.hire_quoted_rate) return '—'
  return `${rupees(l.hire_quoted_rate)} per ${l.hire_rate_basis === 'MONTH' ? 'month' : 'day'}`
}

/**
 * Open (or reopen) a month's log sheet for one hire order. If one is already
 * open for that month the database hands it back rather than making a second.
 */
export function OpenHireLogModal({ open, onClose, woId, orders }: {
  open: boolean
  onClose: () => void
  /** Fixed order (from the order's own page), or null to choose one. */
  woId: string | null
  orders?: { id: string; label: string }[]
}) {
  const navigate = useNavigate()
  const toast = useToast()
  const [chosen, setChosen] = useState('')
  const [month, setMonth] = useState(thisMonth())
  const [busy, setBusy] = useState(false)
  const target = woId ?? chosen

  async function go() {
    if (!target || !month) return
    setBusy(true)
    try {
      const { data, error } = await supabase.rpc('rpc_hire_log_open',
        { p_wo_id: target, p_month: `${month}-01` })
      if (error) throw new Error(dbMessage(error, 'The log sheet could not be opened.'))
      onClose()
      navigate(`/service/hire-logs/${data}`)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'The log sheet could not be opened.')
    } finally { setBusy(false) }
  }

  return (
    <Modal open={open} onClose={onClose}
           title="Open a hire log sheet"
           subtitle="One sheet per order per calendar month. If this month's is already open, you are taken to it."
           footer={
             <>
               <button className="btn-secondary" onClick={onClose}>Cancel</button>
               <button className="btn-primary" disabled={busy || !target || !month}
                       onClick={() => void go()}>
                 {busy ? 'Opening…' : 'Open the sheet'}
               </button>
             </>
           }>
      <div className="space-y-3">
        {!woId && (
          <Field label="Hire order" required
                 hint="Approved equipment hire orders on your sites">
            <select className="field w-full" value={chosen}
                    onChange={(e) => setChosen(e.target.value)}>
              <option value="">Choose…</option>
              {(orders ?? []).map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </Field>
        )}
        <Field label="Month" required hint="Not a month that has not started">
          <Input type="month" value={month} max={thisMonth()}
                 onChange={(e) => setMonth(e.target.value)} />
        </Field>
      </div>
    </Modal>
  )
}

export function WoHire({ woId, woStatus }: { woId: string; woStatus: string }) {
  const { profile } = useAuth()
  const [opening, setOpening] = useState(false)

  const { data: lines } = useQuery<any>(`wo_hire_lines_${woId}`,
    () => supabase.from('wo_line_items')
      .select('id, sl_no, description, uom, rate, quantity, amount, gst_rate_percent, qty_measured, hire_units, '
              + 'hire_rate_basis, hire_quoted_rate, hire_period, with_operator, fuel_by_oval, '
              + 'work_items(hire_by_pieces)')
      .eq('wo_id', woId).eq('pricing_basis', 'EQUIPMENT_HIRE').order('sl_no'),
    [woId], { cacheable: false })

  const { data: progress } = useQuery<any>(`wo_hire_allowed_${woId}`,
    () => supabase.from('v_wo_line_progress').select('wo_line_id, qty_allowed').eq('wo_id', woId),
    [woId], { cacheable: false, idField: 'wo_line_id', enabled: (lines as any[]).length > 0 })

  const { data: logs } = useQuery<any>(`wo_hire_logs_${woId}`,
    () => supabase.from('wo_hire_logs').select('id, period_month, status')
      .eq('wo_id', woId).order('period_month', { ascending: false }),
    [woId], { cacheable: false, enabled: (lines as any[]).length > 0 })

  // GST per machine (CMD, 31-10-2026): the order's own tax answer, and each
  // line's verdict for the rare order that mixes forward and reverse charge.
  const { data: head } = useQuery<any>(`wo_hire_tax_${woId}`,
    () => supabase.from('work_orders')
      .select('id, gst_treatment, is_reverse_charge, gst_amount, contract_value, total_with_gst')
      .eq('id', woId),
    [woId], { cacheable: false, enabled: (lines as any[]).length > 0 })
  const { data: verdicts } = useQuery<any>(`wo_hire_verdicts_${woId}`,
    () => supabase.from('v_rcm_determination').select('wo_line_id, verdict_notified').eq('wo_id', woId),
    [woId], { cacheable: false, idField: 'wo_line_id', enabled: (lines as any[]).length > 0 })

  const [view, setView] = useLinesView(woStatus)

  if ((lines as any[]).length === 0) return null

  const allowed = (id: string) =>
    n((progress as any[]).find((p) => p.wo_line_id === id)?.qty_allowed)
  const canOpen = serviceCan(profile?.role, 'recordMeasurement') && HIRE_LIVE.includes(woStatus)

  const h = (head as any[])[0]
  const taxHead: GstHeader | null = h
    ? { gst_treatment: h.gst_treatment, is_reverse_charge: Boolean(h.is_reverse_charge),
        gst_amount: n(h.gst_amount) }
    : null
  const payerOf = (lineId: string) => (taxHead
    ? gstPayer(taxHead, (verdicts as any[]).find((v) => v.wo_line_id === lineId)?.verdict_notified)
    : 'NONE')

  return (
    <Card title="Machines on hire"
          subtitle={view === 'MONEY'
            ? 'What each machine costs over the days ordered, with its GST split. Paid from the certified hire log sheet.'
            : 'Paid from the hire log sheet: worked, idle and Sunday/holiday days are paid, breakdown days are not. Days below are machine-days - or piece-days for an item hired by the piece, from its delivery and return challans.'}
          actions={
            <div className="flex flex-wrap items-center gap-2">
              {taxHead && <LinesViewSwitch view={view} onChange={setView} progressLabel="Days" />}
              {canOpen && (
                <button className="btn-primary btn-sm" onClick={() => setOpening(true)}>
                  <Plus className="h-4 w-4" /> Open a log sheet
                </button>
              )}
            </div>
          }>
      {view === 'MONEY' && taxHead ? (
        <LineGstTable
          treatment={taxHead.gst_treatment}
          header={{ ...taxHead, contract_value: n(h.contract_value), total_with_gst: n(h.total_with_gst) }}
          rows={(lines as any[]).map((l) => ({
            key: l.id, sl: l.sl_no, title: l.description,
            subtitle: `${qty(l.quantity)} ${l.work_items?.hire_by_pieces ? 'piece' : 'machine'}-days × ${rupees(l.rate)} a day`,
            split: gstSplit(n(l.amount), n(l.gst_rate_percent), payerOf(l.id),
                            taxHead.gst_treatment),
          }))} />
      ) : (
      <div className="overflow-x-auto">
        <table className="tbl">
          <thead>
            <tr>
              <th>#</th><th>Machine</th><th className="num">No.</th><th>Quoted</th>
              <th className="num">Day rate</th><th className="num">Days ordered</th>
              <th className="num">Allowed</th><th className="num">Paid so far</th>
              <th className="num">Left</th>
            </tr>
          </thead>
          <tbody>
            {(lines as any[]).map((l) => (
              <tr key={l.id}>
                <td>{l.sl_no}</td>
                <td className="max-w-[240px]">
                  {l.description}
                  <span className="block text-[11px] text-slate-400">
                    {l.work_items?.hire_by_pieces
                      ? 'Hired by the piece · delivery and return challans'
                      : <>
                          {l.with_operator ? 'With operator' : 'Without operator'}
                          {l.fuel_by_oval ? ' · fuel by OVAL' : ' · fuel by the contractor'}
                        </>}
                  </span>
                </td>
                <td className="num">
                  {l.hire_units}
                  {l.work_items?.hire_by_pieces && <span className="block text-[11px] text-slate-400">pieces</span>}
                </td>
                <td className="whitespace-nowrap text-xs">
                  {quotedLabel(l)}{l.work_items?.hire_by_pieces ? ' a piece' : ''}
                  {/* CMD, 31-10-2026: what the vendor's monthly (or daily)
                      invoice will read, GST included. */}
                  {taxHead && l.hire_quoted_rate && l.work_items?.hire_by_pieces && (() => {
                    // 0220: a piece rate is tiny; what matters is the month's
                    // rent for all the pieces ordered - the quotation's own column.
                    const p = payerOf(l.id)
                    const month = n(l.hire_quoted_rate) * n(l.hire_units) * 30
                    return <span className="block text-[11px] text-slate-500">
                      {rupees(month)} a month for {l.hire_units} pieces
                      {p === 'CHARGED' && <> · {rupees(withGst(month, n(l.gst_rate_percent), p))} incl. GST</>}
                      {p === 'RCM' && <> · + GST by OVAL (RCM)</>}
                    </span>
                  })()}
                  {taxHead && l.hire_quoted_rate && !l.work_items?.hire_by_pieces && (() => {
                    const p = payerOf(l.id)
                    const per = l.hire_rate_basis === 'MONTH' ? 'month' : 'day'
                    if (p === 'CHARGED') {
                      return <span className="block text-[11px] text-slate-500">
                        {rupees(withGst(n(l.hire_quoted_rate), n(l.gst_rate_percent), p))} per {per} incl. GST
                      </span>
                    }
                    if (p === 'RCM') {
                      return <span className="block text-[11px] text-amber-800">
                        + {rupees(n(l.hire_quoted_rate) * n(l.gst_rate_percent) / 100)} GST per {per}, paid by OVAL (RCM)
                      </span>
                    }
                    return null
                  })()}
                </td>
                <td className="num whitespace-nowrap">{rupees(l.rate)}</td>
                <td className="num">{qty(l.quantity)}</td>
                <td className="num">{qty(allowed(l.id))}</td>
                <td className="num font-semibold">{qty(l.qty_measured)}</td>
                <td className="num">{qty(Math.max(allowed(l.id) - n(l.qty_measured), 0))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      <div className="mt-4">
        <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
          <CalendarDays className="h-3.5 w-3.5" /> Log sheets
        </p>
        {(logs as any[]).length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm text-slate-500">
            <Truck className="mr-1 inline h-4 w-4" />
            No log sheet yet. The Site Engineer opens one for each month the machines are on site.
          </p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {(logs as any[]).map((g) => (
              <li key={g.id}>
                <Link to={`/service/hire-logs/${g.id}`}
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-sm hover:bg-slate-50">
                  <span className="font-medium text-oval-700">{monthLabel(g.period_month)}</span>
                  <StatusPill status={g.status} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      <OpenHireLogModal open={opening} onClose={() => setOpening(false)} woId={woId} />
    </Card>
  )
}
